import React, { useState, useMemo, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import CustomVariableItem from './CustomVariableItem';
import VerifyModal from './VerifyModal';
import { extractUDKeys } from './utils';

const CustomVariablesForm = React.memo(function CustomVariablesForm({
  fields,
  customVarFlag,
  meta: { touched, error },
  formValues = {},
  showVerify = false,
}) {
  const [modal, setModal] = useState(false);
  const toggleModal = useCallback(() => setModal((prev) => !prev), []);

  const addCustomVariable = useCallback(() => {
    fields.push({
      key: customVarFlag,
    });
  }, [fields, customVarFlag]);

  const verifyCustomVariables = useMemo(() => {
    const customVariables = fields.getAll() || [];
    const containerUDKeys = extractUDKeys(formValues?.container?.content || '');
    const layoutUDKeys = extractUDKeys(formValues?.layout?.content || '');

    const containerMissingKeys = containerUDKeys.filter(
      (containerUDKey) => !customVariables.some((customVariable) => customVariable.key === containerUDKey.key)
    );
    const layoutMissingKeys = layoutUDKeys.filter(
      (layoutUDKey) => !customVariables.some((customVariable) => customVariable.key === layoutUDKey.key)
    );

    const containerLayoutUDKeys = [...containerUDKeys, ...layoutUDKeys];

    const customVariablesNotInContainerLayout = customVariables.filter(
      (customVariable) =>
        !containerLayoutUDKeys.some((containerLayoutUDKey) => containerLayoutUDKey.key === customVariable.key)
    );

    return { customVariablesNotInContainerLayout, containerMissingKeys, layoutMissingKeys };
  }, [fields, formValues?.container?.content, formValues?.layout?.content]);

  const { customVariablesNotInContainerLayout, containerMissingKeys, layoutMissingKeys } = verifyCustomVariables;

  const customVariableItems = useMemo(() => {
    return fields.map((customVariablesItem, index) => (
      <CustomVariableItem
        key={index}
        customVariablesItem={customVariablesItem}
        index={index}
        fields={fields}
        customVarFlag={customVarFlag}
      />
    ));
  }, [fields, customVarFlag]);

  const fieldsCount = fields.getAll()?.length;

  return (
    <div>
      <Stack direction='row' spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/* Title and count share a baseline, the same lockup CollapsibleCard uses for subtitles. */}
        <Stack direction='row' spacing={1.5} sx={{ alignItems: 'baseline' }}>
          <Typography variant='h6'>Custom Variables</Typography>
          {fieldsCount ? (
            <Typography variant='body2' color='text.secondary'>
              Total {fieldsCount}
            </Typography>
          ) : null}
        </Stack>
        <Stack direction='row' spacing={1}>
          {showVerify && (
            <Button color='success' onClick={toggleModal}>
              Verify Custom Variables
            </Button>
          )}
          {/* Labeled like every other section's add button, not a bare floating "+". */}
          <Button color='success' onClick={addCustomVariable} startIcon={<Add />}>
            Add Variable
          </Button>
        </Stack>
      </Stack>
      {touched && error && (
        <Typography variant='body2' color='error' sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      {customVariableItems}
      <VerifyModal
        isOpen={modal}
        toggle={toggleModal}
        customVarFlag={customVarFlag}
        layoutMissingKeys={layoutMissingKeys}
        containerMissingKeys={containerMissingKeys}
        customVariablesNotInContainerLayout={customVariablesNotInContainerLayout}
        fields={fields}
      />
    </div>
  );
});

export default CustomVariablesForm;
